import { Component, OnInit, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { UsersService, CreateUserRequest, Organization } from '../../../shared/services/users/users.service';
import { ToasterService } from '../../../shared/services/toaster/toaster.service';
import { UserRoleService } from '../../../shared/services/user-role/user-role.service';
import { MeService, InviteContext } from '../../../shared/services/me/me.service';
import { OrganizationGroupService } from '../../../shared/services/organization-group/organization-group.service';

@Component({
  selector: 'app-new-user',
  templateUrl: './new-user.component.html',
  styleUrls: ['./new-user.component.scss']
})
export class NewUserComponent implements OnInit, OnChanges {
  @Input() isOpen: boolean = false;
  @Input() householdId: number | null = null;
  /** When set, new users are created under this org instead of the current user's org. */
  @Input() overrideOrganizationId: number | null = null;
  @Input() showRoleSelection: boolean = false;
  @Output() close = new EventEmitter<void>();
  @Output() userCreated = new EventEmitter<void>();

  userData: CreateUserRequest = {
    EMAIL: '',
    FIRST_NAME: '',
    LAST_NAME: '',
    HOUSEHOLD_ID: '',
    ORGANIZATION_ID: 0,
    ORGANIZATION_GROUP_ID: undefined,
    ROLE: 'ADVISOR'
  };

  roleOptions: { value: string; label: string }[] = [];
  organizationOptions: { value: number; label: string; groupId: number | null | undefined }[] = [];

  inviteContext: InviteContext | null = null;
  isLoadingContext: boolean = false;
  isSubmitting: boolean = false;

  constructor(
    private usersService: UsersService,
    private toaster: ToasterService,
    private userRoleService: UserRoleService,
    private meService: MeService,
    private orgGroupService: OrganizationGroupService,
  ) { }

  ngOnInit(): void {
    void this.loadInviteContext();
  }

  ngOnChanges(): void {
    if (this.isOpen) {
      this.resetForm();
    }

    if (this.householdId) {
      this.userData.HOUSEHOLD_ID = this.householdId.toString();
      this.userData.ROLE = 'ADVISOR';
    } else if (this.showRoleSelection) {
      this.userData.ROLE = this.roleOptions[0]?.value ?? 'ADVISOR';
    }
  }

  private async loadInviteContext(): Promise<void> {
    this.isLoadingContext = true;
    try {
      this.inviteContext = await this.meService.getInviteContext();
      this.buildRoleOptions();

      if (!this.inviteContext.organizationLocked) {
        await this.loadOrganizationOptions();
      }

      this.applyContextToForm();
    } catch (error: unknown) {
      console.error('Error loading invite context:', error);
      // Fall back to basic behavior using current user org
      await this.fallbackToCurrentUserOrg();
    } finally {
      this.isLoadingContext = false;
    }
  }

  private async loadOrganizationOptions(): Promise<void> {
    try {
      const result = await this.orgGroupService.listOrganizations(1, 500).toPromise();
      this.organizationOptions = (result?.data ?? []).map((org: Organization) => ({
        value: org.ORGANIZATION_ID,
        label: org.ORGANIZATION_NAME,
        groupId: org.GROUP_ID,
      }));
    } catch (error: unknown) {
      console.error('Error loading organizations:', error);
    }
  }

  private applyContextToForm(): void {
    if (!this.inviteContext) return;

    if (this.inviteContext.organizationLocked && this.inviteContext.organizationId) {
      this.userData.ORGANIZATION_ID = this.overrideOrganizationId ?? this.inviteContext.organizationId;
      if (this.inviteContext.organizationGroupId) {
        this.userData.ORGANIZATION_GROUP_ID = this.inviteContext.organizationGroupId;
      }
    }
  }

  private async fallbackToCurrentUserOrg(): Promise<void> {
    try {
      const userResponse = await this.usersService.getCurrentUser();
      this.userData.ORGANIZATION_ID = this.overrideOrganizationId ?? userResponse.ORGANIZATION?.ORGANIZATION_ID ?? 0;
    } catch (error: unknown) {
      console.error('Error fetching current user organization:', error);
    }
    this.buildRoleOptions();
  }

  private buildRoleOptions(): void {
    if (this.inviteContext) {
      this.roleOptions = this.inviteContext.allowedRoles.map(role => ({
        value: role,
        label: this.getRoleLabel(role),
      }));
    } else {
      const base: { value: string; label: string }[] = [
        { value: 'ADVISOR', label: 'Advisor' },
        { value: 'MANAGER', label: 'Manager' }
      ];
      if (this.userRoleService.isSupervisor() || this.userRoleService.isSuperAdmin()) {
        base.push({ value: 'SUPERVISOR', label: 'Supervisor' });
      }
      this.roleOptions = base;
    }
  }

  private getRoleLabel(role: string): string {
    const labels: Record<string, string> = {
      ADVISOR: 'Advisor',
      MANAGER: 'Manager',
      SUPERVISOR: 'Supervisor',
      SUPER_ADMIN: 'Super Admin',
    };
    return labels[role] ?? role;
  }

  /** Called when the super admin changes the org dropdown. */
  onOrganizationChange(orgId: number): void {
    this.userData.ORGANIZATION_ID = Number(orgId);
    const selectedOrg = this.organizationOptions.find(o => o.value === Number(orgId));
    this.userData.ORGANIZATION_GROUP_ID = selectedOrg?.groupId ?? undefined;
  }

  get derivedGroupName(): string | null {
    if (this.inviteContext?.organizationGroupLocked) {
      return this.inviteContext.organizationGroupName;
    }
    if (this.userData.ORGANIZATION_GROUP_ID) {
      // For super admin: derive group name from selected org
      const selectedOrg = this.organizationOptions.find(
        o => o.value === this.userData.ORGANIZATION_ID
      );
      if (selectedOrg?.groupId === this.userData.ORGANIZATION_GROUP_ID) {
        // Group name not available client-side without a separate lookup; show ID as fallback
        return null;
      }
    }
    return null;
  }

  get showOrganizationField(): boolean {
    return !this.householdId;
  }

  get showOrganizationGroupField(): boolean {
    if (this.householdId) return false;
    if (this.inviteContext?.organizationGroupLocked) {
      return !!this.inviteContext.organizationGroupId;
    }
    return true;
  }

  public closeModal(): void {
    this.close.emit();
    this.resetForm();
  }

  private resetForm(): void {
    this.buildRoleOptions();

    const orgId = this.overrideOrganizationId
      ?? this.inviteContext?.organizationId
      ?? 0;

    this.userData = {
      EMAIL: '',
      FIRST_NAME: '',
      LAST_NAME: '',
      HOUSEHOLD_ID: this.householdId ? this.householdId.toString() : '',
      ORGANIZATION_ID: orgId,
      ORGANIZATION_GROUP_ID: this.inviteContext?.organizationGroupId ?? undefined,
      ROLE: this.roleOptions[0]?.value ?? 'ADVISOR',
    };

    this.isSubmitting = false;
  }

  public async submitForm(): Promise<void> {
    if (!this.validateForm()) {
      return;
    }

    this.isSubmitting = true;

    try {
      await this.usersService.createUser(this.userData);
      this.toaster.success('User created successfully', 3000);
      this.userCreated.emit();
      this.resetForm();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      this.toaster.warning(`Error creating user: ${message}`, 4000);
      console.error('Error creating user:', error);
    } finally {
      this.isSubmitting = false;
    }
  }

  private validateForm(): boolean {
    if (!this.userData.FIRST_NAME) {
      this.toaster.warning('First name is required', 3000);
      return false;
    }

    if (!this.userData.LAST_NAME) {
      this.toaster.warning('Last name is required', 3000);
      return false;
    }

    if (!this.userData.EMAIL) {
      this.toaster.warning('Email is required', 3000);
      return false;
    }

    if (!this.userData.ORGANIZATION_ID) {
      this.toaster.warning('Organization is required', 3000);
      return false;
    }

    return true;
  }
}
